/**
 * `React.lazy` wrapper that recovers from chunk-load failures on web/desktop.
 *
 * The web/desktop build code-splits each screen into its own webpack chunk
 * (`src_screens_<name>_tsx.bundle.<hash>.js`). When a new deploy ships, the
 * `index.html` cached in a long-lived browser tab still references the OLD
 * chunk hashes — but `clean: true` in webpack.config + immutable cache
 * headers on S3 mean those files are gone. The request falls through to
 * S3's website-routing catch-all, which returns `index.html` (200) for any
 * missing path. The browser then tries to parse `<!DOCTYPE html>` as JS:
 *
 *     Uncaught SyntaxError: Unexpected token '<'
 *       at src_screens_ProfileScreen_tsx.bundle.js:1:1
 *
 * Recovery: retry the dynamic import once (transient network blip), then
 * force a hard reload so the browser fetches the fresh `index.html` with
 * the new manifest. `sessionStorage` debounces back-to-back reloads so a
 * chunk that's *actually* broken (not just stale) can't trap the user in
 * a reload loop.
 *
 * On native (iOS/Android) all screens are statically bundled, so there are
 * no chunk requests to fail. We fall through to plain `React.lazy`.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { Platform } from 'react-native';

const RELOAD_KEY = '__el_chunk_reloaded_at';
const RELOAD_DEBOUNCE_MS = 30_000;

type Factory<T extends ComponentType<any>> = () => Promise<{ default: T }>;

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: Factory<T>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (Platform.OS !== 'web') throw err;
      try {
        return await factory();
      } catch (err2) {
        let alreadyReloaded = false;
        try {
          const last = Number(window.sessionStorage?.getItem(RELOAD_KEY) || 0);
          alreadyReloaded = Date.now() - last < RELOAD_DEBOUNCE_MS;
          if (!alreadyReloaded) {
            window.sessionStorage?.setItem(RELOAD_KEY, String(Date.now()));
          }
        } catch { /* sessionStorage may be unavailable in private mode */ }

        if (!alreadyReloaded) {
          try { window.location.reload(); } catch { /* no-op */ }
        }
        throw err2;
      }
    }
  });
}
